import { listStatusKey } from '@/lib/quoteStatus'
import { getApi } from '@/stores/api'
import { useInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button, Input, NativeSelect } from '@repo/ui/components'
import { capitalize, formatUsDate, formatUsTime } from '@repo/utils'
import { FileText, Loader2, SearchIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'

type QuotesSearch = { office?: string }

export const Route = createFileRoute('/_contractor/quotes/')({
  // PET-2999: one list across every office the company works with. `office`
  // narrows it, and is also what an emailed link from before the merge lands on.
  validateSearch: (search: Record<string, unknown>): QuotesSearch => ({
    office: typeof search.office === 'string' ? search.office : undefined,
  }),
  component: QuotesListPage,
})

function getStatusBadgeClasses(status: string | null) {
  switch (status) {
    case 'sent':
      return 'bg-amber-100 text-amber-700'
    case 'approved':
      return 'bg-green-100 text-green-700'
    case 'rejected':
      return 'bg-red-100 text-red-700'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

function getEffectiveStatus(approbationState: string | null, status: string | null) {
  if (approbationState === 'approved') return 'approved'
  if (approbationState === 'rejected') return 'rejected'
  return status
}

function QuotesListPage() {
  const { t } = useTranslation('quotes')
  const api = getApi()
  const navigate = useNavigate()
  const { office } = Route.useSearch()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setDebouncedSearch(value)
  }, 300)

  useEffect(() => {
    debouncedSetSearch(search)
  }, [search, debouncedSetSearch])

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['contractor-quotes', debouncedSearch, office ?? null],
    queryFn: async ({ pageParam }) => {
      const res = await api.contractor.quotes.$get({
        query: {
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(office ? { office } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      })
      if (!res.ok) throw new Error('Failed to fetch quotes')
      return res.json()
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  })

  const pages = data?.pages ?? []
  const quotes = pages.flatMap((page) => page.quotes)
  // Every page carries the same office set; the first one is enough.
  const offices = pages[0]?.offices ?? []
  // Drives the office filter AND the office column: a company that buys from
  // one office should see neither.
  const hasMultipleOffices = offices.length > 1
  const activeOfficeName = offices.find((o) => o.slug === office)?.name
  // A slug the office set no longer contains: a stale emailed link, or an
  // office that withdrew its `customer` partnership. The server already answers
  // this correctly with an empty list — without saying so here the picker would
  // read "All organizations" over a list filtered to nothing.
  const isUnknownOffice = Boolean(office) && !activeOfficeName

  const getEmptyStateMessage = () => {
    if (isUnknownOffice) return t('list.empty.officeNotShared')
    if (debouncedSearch) return t('list.empty.noMatch')
    return t('list.empty.none')
  }

  const getStatusLabel = (approbationState: string | null, status: string | null) =>
    t(listStatusKey(approbationState, status), { defaultValue: capitalize(status ?? '') })

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold">{t('list.title')}</h1>
        <p className="mt-1 text-muted-foreground">
          {activeOfficeName
            ? t('list.subtitleOffice', { office: activeOfficeName })
            : t('list.subtitle')}
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative max-w-75 min-w-50 flex-1">
          <SearchIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('list.searchPlaceholder')}
            className="pl-10"
          />
        </div>
        {(hasMultipleOffices || isUnknownOffice) && (
          <NativeSelect
            aria-label={t('list.officeFilter.label')}
            className="w-auto"
            value={office ?? ''}
            onChange={(e) =>
              navigate({
                to: '/quotes',
                search: e.target.value ? { office: e.target.value } : {},
              })
            }
          >
            <option value="">{t('list.officeFilter.all')}</option>
            {/* Rendered so the control shows what it is actually filtered by,
                and so there is a way back to the unfiltered list. */}
            {isUnknownOffice && office && (
              <option value={office}>{t('list.officeFilter.notShared', { office })}</option>
            )}
            {offices.map((o) => (
              <option key={o.id} value={o.slug}>
                {o.name}
              </option>
            ))}
          </NativeSelect>
        )}
        {data && (
          <div className="ml-auto text-sm text-muted-foreground">
            {hasNextPage
              ? t('list.countSoFar', { count: quotes.length })
              : t('list.count', { count: quotes.length })}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      ) : quotes.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
          <FileText className="size-12 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{getEmptyStateMessage()}</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          <div className="relative w-full overflow-auto">
            <table className="w-full caption-bottom text-sm">
              <thead className="bg-muted/50 [&_tr]:border-b">
                <tr className="border-b transition-colors">
                  <th className="h-10 px-4 text-left align-middle font-semibold text-muted-foreground">
                    {t('list.columns.number')}
                  </th>
                  <th className="h-10 px-4 text-left align-middle font-semibold text-muted-foreground">
                    {t('list.columns.jobName')}
                  </th>
                  {hasMultipleOffices && (
                    <th className="h-10 px-4 text-left align-middle font-semibold text-muted-foreground">
                      {t('list.columns.office')}
                    </th>
                  )}
                  <th className="h-10 px-4 text-left align-middle font-semibold text-muted-foreground">
                    {t('list.columns.sentBy')}
                  </th>
                  <th className="h-10 px-4 text-left align-middle font-semibold text-muted-foreground">
                    {t('list.columns.status')}
                  </th>
                  <th className="h-10 px-4 text-left align-middle font-semibold text-muted-foreground">
                    {t('list.columns.decidedBy')}
                  </th>
                  <th className="h-10 px-4 text-left align-middle font-semibold text-muted-foreground">
                    {t('list.columns.received')}
                  </th>
                  <th className="h-10 px-4 text-left align-middle font-semibold text-muted-foreground">
                    {t('list.columns.updated')}
                  </th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {quotes.map((quote, index) => {
                  const effectiveStatus = getEffectiveStatus(quote.approbationState, quote.status)
                  const receivedDate = new Date(quote.createdAt)
                  const updatedDate = new Date(quote.updatedAt)

                  return (
                    <tr
                      key={quote.id}
                      className={`cursor-pointer border-b transition-colors hover:bg-muted/50 ${index % 2 === 0 ? 'bg-background' : 'bg-muted/30'}`}
                      onClick={() =>
                        navigate({
                          to: '/$tenantSlug/quotes/$quoteNumber',
                          // The office stays in the path: quote numbers are
                          // unique per office, not globally, so a merged list
                          // can hold two quotes numbered the same.
                          params: {
                            tenantSlug: quote.office.slug,
                            quoteNumber: quote.quoteNumber,
                          },
                        })
                      }
                    >
                      <td className="p-4 py-2 align-middle whitespace-nowrap">
                        <span className="font-mono text-xs text-muted-foreground">
                          {quote.quoteNumber}
                        </span>
                      </td>
                      <td className="p-4 py-2 align-middle whitespace-nowrap">
                        <span className="font-medium">{quote.jobName || '-'}</span>
                      </td>
                      {hasMultipleOffices && (
                        <td className="p-4 py-2 align-middle whitespace-nowrap">
                          <span className="text-muted-foreground">{quote.office.name}</span>
                        </td>
                      )}
                      <td className="p-4 py-2 align-middle whitespace-nowrap">
                        <span className="text-muted-foreground">
                          {quote.salespersonName || '-'}
                        </span>
                      </td>
                      <td className="p-4 py-2 align-middle whitespace-nowrap">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadgeClasses(effectiveStatus)}`}
                        >
                          {getStatusLabel(quote.approbationState, quote.status)}
                        </span>
                      </td>
                      <td className="p-4 py-2 align-middle whitespace-nowrap">
                        <span className="text-muted-foreground">
                          {quote.approbationByName || '-'}
                        </span>
                      </td>
                      <td className="p-4 py-2 align-middle whitespace-nowrap">
                        <div>
                          <p>{formatUsDate(receivedDate)}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatUsTime(receivedDate)}
                          </p>
                        </div>
                      </td>
                      <td className="p-4 py-2 align-middle whitespace-nowrap">
                        <div>
                          <p>{formatUsDate(updatedDate)}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatUsTime(updatedDate)}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {hasNextPage && (
            <div className="flex justify-center border-t p-4">
              <Button
                variant="outline"
                size="sm"
                onPress={() => void fetchNextPage()}
                isDisabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? t('list.loadingMore') : t('list.loadMore')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
